/**
 * provider-registry.ts — THE single source of provider display data
 * (logo, brand color, display name) for the whole app.
 *
 * Provider BRAND assets are bundled images + Tailwind classes, keyed by a
 * NORMALIZED provider key. sphinx has two providers; an unknown key falls back
 * to a monogram.
 *
 * This is a LEAF module — it must not import from model-helpers (which delegates
 * back here) or any component, to avoid a cycle.
 */

import claudeLogo from "/claude_logo.png";
import openaiLogo from "/openai_logo.png";

export type ThemeMode = "light" | "dark" | "system";

export interface ProviderBrand {
  displayName: string;
  /** Bundled logo; `undefined` → render the monogram. */
  logoLight?: string;
  /** Dark-theme variant; falls back to `logoLight`. */
  logoDark?: string;
  /** Monogram styling: a Tailwind text-color class pair. */
  color: string;
}

/**
 * Canonical provider registry. Keys are NORMALIZED (see normalizeProviderKey).
 */
const PROVIDER_REGISTRY: Record<string, ProviderBrand> = {
  anthropic: {
    displayName: "Anthropic",
    logoLight: claudeLogo,
    color: "text-orange-600 dark:text-orange-400",
  },
  openai: {
    displayName: "OpenAI",
    logoLight: openaiLogo,
    color: "text-green-600 dark:text-green-400",
  },
};

const DEFAULT_COLOR = "text-gray-600 dark:text-gray-400";

/** Title-case an unknown provider key ("arcee-ai" → "Arcee Ai"). */
export function titleCaseProviderKey(raw: string): string {
  return raw
    .split(/[-_]/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/** Collapse any raw provider string to its canonical registry key. */
export function normalizeProviderKey(raw: string): string {
  return raw.toLowerCase();
}

function resolveDark(themeMode?: ThemeMode): boolean {
  if (themeMode === "dark") return true;
  if (themeMode === "light" || themeMode === undefined) return false;
  // system: read the document class (SSR-safe guard).
  return (
    typeof document !== "undefined" &&
    document.documentElement.classList.contains("dark")
  );
}

/** Theme-aware provider logo, or `null` when none is registered (→ monogram). */
export function getProviderLogo(
  provider: string,
  themeMode?: ThemeMode
): string | null {
  const brand = PROVIDER_REGISTRY[normalizeProviderKey(provider)];
  if (!brand?.logoLight) return null;
  return resolveDark(themeMode)
    ? brand.logoDark ?? brand.logoLight
    : brand.logoLight;
}

/** Monogram color classes for a provider (see ProviderBrand.color). */
export function getProviderColor(provider: string): string {
  return (
    PROVIDER_REGISTRY[normalizeProviderKey(provider)]?.color ?? DEFAULT_COLOR
  );
}

/**
 * Human display name for a provider key: the registry entry, or a title-cased
 * fallback for an unknown key.
 */
export function getProviderDisplayName(groupKey: string): string {
  const brand = PROVIDER_REGISTRY[normalizeProviderKey(groupKey)];
  return brand?.displayName ?? titleCaseProviderKey(groupKey);
}

/** Whether a provider has a bundled logo (true) or falls back to a monogram. */
export function providerHasLogo(provider: string): boolean {
  return !!PROVIDER_REGISTRY[normalizeProviderKey(provider)]?.logoLight;
}
